import { fetchTracks, type Track } from "../api.js";
import { audioContext } from "../audio/context.js";
import { TEMPO_RANGES, type Deck } from "../audio/deck.js";
import type { Mixer } from "../audio/mixer.js";
import {
  DEFAULT_PLANNER_CONFIG,
  planTransition,
  type PlanInput,
  type PlannerConfig,
  type TransitionPlan,
} from "./planner.js";
import {
  DEFAULT_SELECTOR_CONFIG,
  Selector,
  type Candidate,
  type SelectorConfig,
} from "./selector.js";
import { poolRefusal, selectablePool } from "./pool.js";
import { chooseSegment, effectiveBounds, type Segment } from "./tails.js";
import { Ticker } from "./ticker.js";

/**
 * How often the heartbeat fires. Deliberately coarse — it only decides whether
 * to arm something, never does audio work.
 */
const TICK_MS = 2000;

/**
 * Lead times. Both must comfortably exceed the worst case throttled tick
 * interval (about 60s in a background tab), or a transition could be armed too
 * late to schedule. Preload must also exceed worst-case fetch and decode (C9).
 */
const PRELOAD_LEAD_SECONDS = 120;
const ARM_LEAD_SECONDS = 75;

export interface AutoMixConfig extends SelectorConfig, PlannerConfig {
  /**
   * Longest a single track may occupy the mix. Beyond this a random excerpt is
   * played instead — one 23-minute piece would otherwise be most of an hour.
   * Zero disables excerpting.
   */
  maxSegmentSeconds: number;
}

/**
 * Planning and selection must both use the deck's widest actual fader range.
 * Hardcoding a range here is how the planner came to authorise a match the deck
 * could not reach.
 */
const MAX_TEMPO_RANGE = Math.max(...TEMPO_RANGES);

export const DEFAULT_AUTOMIX_CONFIG: AutoMixConfig = {
  ...DEFAULT_SELECTOR_CONFIG,
  ...DEFAULT_PLANNER_CONFIG,
  tempoRange: MAX_TEMPO_RANGE,
  maxSegmentSeconds: 420,
};

export interface HistoryEntry {
  title: string;
  method: string;
  reason: string;
}

export interface AutoMixSnapshot {
  armed: boolean;
  live: string | null;
  nowPlaying: Track | null;
  next: Track | null;
  nextState: "none" | "selecting" | "loading" | "ready" | "failed";
  /** Seconds until the transition begins, or null if not yet planned. */
  countdown: number | null;
  plannedMethod: string | null;
  plannedReason: string | null;
  queued: number[];
  history: HistoryEntry[];
  usingWorker: boolean;
  message: string | null;
  /** Tracks auto-mix can actually choose between: analysed, present, not excluded. */
  poolSize: number;
  /** The tag the pool is restricted to, or null for the whole library. */
  poolTag: string | null;
}

/**
 * Drives the decks and mixer to keep music playing unattended.
 *
 * It only uses the same public operations the UI does, so anything auto-mix can
 * do the operator can do by hand, and neither can reach past the other into the
 * audio graph.
 */
export class AutoMix {
  private readonly selector = new Selector();
  private readonly ticker: Ticker;
  private readonly decks: Record<string, Deck>;

  private armed = false;
  private tag: string | null = null;
  private liveId: string;
  private nextTrack: Track | null = null;
  private nextState: AutoMixSnapshot["nextState"] = "none";
  private plan: TransitionPlan | null = null;
  private planEndsAt = 0;
  private rampEndsAt = 0;
  private history: HistoryEntry[] = [];
  private message: string | null = null;
  private library: Track[] = [];
  private busy = false;
  private manualHold = false;
  /** The region of each loaded track being played, keyed by deck id. */
  private segments = new Map<string, Segment>();
  private ramping = false;
  /** A skip the operator has asked for, honoured as soon as the loop can. */
  private pendingSkip: "smooth" | "aggressive" | null = null;
  private choosing = false;

  config: AutoMixConfig = { ...DEFAULT_AUTOMIX_CONFIG };

  constructor(
    deckA: Deck,
    deckB: Deck,
    private readonly mixer: Mixer,
    private readonly onChange: (snapshot: AutoMixSnapshot) => void,
  ) {
    this.decks = { [deckA.id]: deckA, [deckB.id]: deckB };
    this.liveId = deckA.id;
    this.ticker = new Ticker(TICK_MS, () => void this.tick());
  }

  // -- library ----------------------------------------------------------

  setLibrary(tracks: Track[]): void {
    // `selectablePool` is the single definition of what auto-mix may pick, so
    // `poolSize` below cannot disagree with what the selector was given.
    this.library = selectablePool(tracks);
    const candidates: Candidate[] = this.library.map((t) => ({ id: t.id, tempo: t.tempo }));
    this.selector.setLibrary(candidates);
    this.emit();
  }

  /**
   * How many tracks auto-mix can actually choose between.
   *
   * The single number the UI must show. Displaying a tag's total track count
   * next to an auto-mix that can only reach the analysed ones is the same class
   * of mismatch that has bitten this project repeatedly, so there is one
   * accessor and both sides read it.
   */
  get poolSize(): number {
    return this.library.length;
  }

  get poolTag(): string | null {
    return this.tag;
  }

  /**
   * Restrict the pool to a tag, or pass null for the whole library.
   *
   * The filter is applied server-side so the pool is not silently capped by the
   * page limit: a tag's tracks could otherwise fall outside the first 1000 rows
   * and simply never be selected.
   */
  async refreshLibrary(tag: string | null = this.tag): Promise<void> {
    this.tag = tag;
    const page = await fetchTracks({ limit: 1000, ...(tag ? { tag } : {}) });
    this.setLibrary(page.items);
  }

  private track(id: number): Track | null {
    return this.library.find((t) => t.id === id) ?? null;
  }

  // -- arming -----------------------------------------------------------

  arm(): void {
    if (this.armed) return;

    // Refuse rather than quietly falling back to the whole library. A pool that
    // differs from the one on screen is the failure this project keeps making,
    // and here it would show up as auto-mix playing tracks the operator
    // deliberately filtered out.
    const refusal = poolRefusal(this.poolSize, this.tag);
    if (refusal !== null) {
      this.message = refusal;
      this.emit();
      return;
    }

    this.armed = true;
    this.manualHold = false;
    this.message = null;
    this.ticker.start();
    void this.tick();
    this.emit();
  }

  disarm(): void {
    if (!this.armed) return;
    this.armed = false;
    this.pendingSkip = null;
    this.ticker.stop();
    this.mixer.cancelScheduledCrossfade();
    this.plan = null;
    this.emit();
  }

  toggle(): void {
    if (this.armed) this.disarm();
    else this.arm();
  }

  /**
   * Called when the operator touches a control (X2).
   *
   * A pending transition that has not begun is abandoned, so auto-mix is not
   * fighting a hand on the crossfader. One that has already started is allowed
   * to finish, because cancelling mid-fade would be more surprising than
   * completing.
   */
  notifyManualControl(): void {
    if (!this.armed || !this.plan) return;
    if (audioContext().currentTime < this.plan.startAt) {
      this.mixer.cancelScheduledCrossfade();
      this.plan = null;
      this.manualHold = true;
      this.message = "Transition cancelled — manual control taken.";
      this.emit();
    }
  }

  // -- queue ------------------------------------------------------------

  enqueue(trackId: number): void {
    this.selector.enqueue(trackId);
    this.emit();
  }

  dequeue(trackId: number): void {
    this.selector.dequeue(trackId);
    this.emit();
  }

  /** Replace the pending next track with a uniformly random one (X8a). */
  mixItUp(): void {
    if (this.plan) {
      this.mixer.cancelScheduledCrossfade();
      this.plan = null;
    }
    this.nextTrack = null;
    this.nextState = "none";
    this.message = "Picking something different…";
    this.emit();
    void this.tick();
  }

  // -- the loop ---------------------------------------------------------

  private liveDeck(): Deck {
    return this.decks[this.liveId] as Deck;
  }

  private idleDeck(): Deck {
    const other = Object.keys(this.decks).find((id) => id !== this.liveId) as string;
    return this.decks[other] as Deck;
  }

  private async tick(): Promise<void> {
    if (!this.armed || this.busy) return;
    this.busy = true;
    try {
      await this.step();
    } catch (err) {
      this.message = err instanceof Error ? err.message : String(err);
    } finally {
      this.busy = false;
      this.emit();
    }

    // A skip that arrived mid-tick, or that had to wait for a load, is picked
    // up straight away rather than waiting out the heartbeat interval.
    if (this.pendingSkip && !this.busy) {
      const again = this.pendingSkip;
      queueMicrotask(() => {
        if (this.pendingSkip === again) void this.tick();
      });
    }
  }

  private async step(): Promise<void> {
    const ctx = audioContext();
    const live = this.liveDeck();
    const liveSnapshot = live.snapshot();

    // Finish a transition whose scheduled end has passed. Being a tick late
    // here is harmless: the audio already did the right thing on its own clock.
    if (this.plan && ctx.currentTime >= this.planEndsAt) {
      this.completeTransition();
      return;
    }
    if (this.plan) return;

    // A completed bend leaves the decks matched; re-plan the blend properly.
    // A pending skip abandons the bend instead of waiting it out.
    if (this.ramping) {
      if (this.pendingSkip) this.ramping = false;
      else if (ctx.currentTime < this.rampEndsAt) return;
      else this.ramping = false;
    }

    if (this.pendingSkip) {
      await this.honourSkip();
      return;
    }

    if (liveSnapshot.state !== "playing") {
      // Nothing playing: start something rather than sit silent (X3).
      if (liveSnapshot.state === "ready" && liveSnapshot.track) {
        await live.togglePlay();
        return;
      }
      if (liveSnapshot.state === "empty") {
        await this.startFirstTrack();
        return;
      }
      return;
    }

    const segment = this.segmentFor(live);
    const remaining = (segment.end - live.position()) / liveSnapshot.rate;

    if (!this.nextTrack && remaining <= PRELOAD_LEAD_SECONDS) {
      await this.chooseNext(false);
      return;
    }

    if (
      this.nextTrack &&
      this.nextState === "ready" &&
      !this.manualHold &&
      remaining <= ARM_LEAD_SECONDS
    ) {
      this.armTransition();
    }
  }

  /**
   * Where in a track to start, chosen before it is loaded.
   *
   * The audible bounds come from peak data that is only available after
   * loading, so this uses the whole duration and lets `segmentFor` refine the
   * ends once the peaks arrive. The point here is only to avoid fetching and
   * decoding forty minutes of audio to play ten.
   */
  private plannedSegment(track: Track): { start: number; duration: number } | undefined {
    const cap = this.config.maxSegmentSeconds;
    const duration = track.duration ?? 0;
    if (!(cap > 0) || duration <= cap) return undefined;

    const latest = duration - cap;
    return { start: Math.random() * latest, duration: cap };
  }

  /**
   * The region of a deck's track to play: audible bounds, then capped to a
   * random excerpt if the track is very long.
   */
  private segmentFor(deck: Deck): Segment {
    const existing = this.segments.get(deck.id);
    if (existing) return existing;

    const snapshot = deck.snapshot();
    const duration = snapshot.duration || snapshot.track?.duration || 0;
    const bounds =
      deck.peaksOverview && duration > 0
        ? effectiveBounds(deck.peaksOverview, duration)
        : { start: 0, end: duration };

    // Only the loaded region is playable, so the segment is intersected with
    // it - otherwise a transition could be planned into audio not in memory.
    const held = deck.window();
    const clipped = {
      start: Math.max(bounds.start, held.offset),
      end: Math.min(bounds.end, deck.loadedEnd()),
    };
    const segment = chooseSegment(
      clipped,
      this.config.maxSegmentSeconds,
      Array.from(deck.beats?.beats ?? []),
      this.config.beatsPerBar,
    );
    this.segments.set(deck.id, segment);
    return segment;
  }

  private async startFirstTrack(): Promise<void> {
    const chosen = this.selector.next(null, this.config);
    if (!chosen) {
      this.message = "No analysed tracks available to play.";
      return;
    }
    const track = this.track(chosen.id);
    if (!track) return;
    this.selector.markPlayed(track.id);
    const live = this.liveDeck();
    this.segments.delete(live.id);
    await live.load(track, this.plannedSegment(track));
    if (live.snapshot().state !== "ready") return;
    // Start at the chosen segment, not at zero: for a long track the segment is
    // an excerpt from the middle, and for any track it skips a silent lead-in.
    const segment = this.segmentFor(live);
    live.seek(segment.start);
    await live.togglePlay();
  }

  /**
   * Carry out a requested skip, or keep waiting until it can be carried out.
   *
   * Every exit reports something. Silently returning was most of why the
   * buttons looked broken: three separate paths gave up without a word.
   */
  private nextReady(): boolean {
    return this.nextState === "ready";
  }

  private async honourSkip(): Promise<void> {
    const urgency = this.pendingSkip;
    if (!urgency) return;

    const live = this.liveDeck().snapshot();
    if (live.state !== "playing") {
      this.pendingSkip = null;
      this.message = "Nothing is playing to skip.";
      return;
    }

    if (!this.nextReady()) {
      if (!this.choosing) await this.chooseNext(false);
      if (!this.nextReady()) {
        // Not given up on - the loop will try again on the next pass.
        this.message = "Skip queued — waiting for the next track to load.";
        return;
      }
    }

    this.pendingSkip = null;
    this.manualHold = false;
    if (this.plan) {
      this.mixer.cancelScheduledCrossfade();
      this.plan = null;
    }

    this.armTransition(urgency);
    if (!this.plan) {
      this.message = "Could not start that transition — will retry.";
      this.pendingSkip = urgency;
    }
  }

  private async chooseNext(ignoreTempo: boolean): Promise<void> {
    // Two overlapping loads on one deck abort each other and leave the next
    // track in limbo, which is what made a mistimed button press fail.
    if (this.choosing) return;
    this.choosing = true;
    this.nextState = "selecting";
    this.emit();

    const live = this.liveDeck().snapshot();
    const exclude = [live.track?.id].filter((id): id is number => typeof id === "number");

    // Up to a few attempts, so one unloadable track does not stall the mix (X3).
    for (let attempt = 0; attempt < 3; attempt++) {
      const chosen = this.selector.next(live.effectiveBpm ?? null, this.config, {
        ignoreTempo,
        exclude,
      });
      if (!chosen) break;

      const track = this.track(chosen.id);
      if (!track) continue;

      this.nextTrack = track;
      this.nextState = "loading";
      this.manualHold = false;
      this.emit();

      const idle = this.idleDeck();
      this.segments.delete(idle.id);
      // Load only the region that will be played. Whole-track loading is what
      // made long sets impractical: a 48-minute set is over a gigabyte decoded.
      await idle.load(track, this.plannedSegment(track));
      if (idle.snapshot().state === "ready") {
        this.selector.markPlayed(track.id);
        this.nextState = "ready";
        this.message = null;
        this.choosing = false;
        this.emit();
        return;
      }

      // Failed to load: record it, exclude it, and try another.
      this.message = `Skipped ${track.title ?? track.path}: ${idle.snapshot().error ?? "load failed"}`;
      exclude.push(track.id);
      this.selector.markPlayed(track.id);
    }

    this.nextTrack = null;
    this.nextState = "failed";
    this.choosing = false;
  }

  private armTransition(urgency: PlanInput["urgency"] = "scheduled"): void {
    const live = this.liveDeck();
    const idle = this.idleDeck();
    const liveSnapshot = live.snapshot();
    const idleSnapshot = idle.snapshot();
    if (!this.nextTrack || idleSnapshot.state !== "ready") return;

    const outgoingSegment = this.segmentFor(live);
    const incomingSegment = this.segmentFor(idle);

    const plan = planTransition({
      now: audioContext().currentTime,
      outgoing: {
        beats: Array.from(live.beats?.beats ?? []),
        tempo: liveSnapshot.baseBpm,
        start: outgoingSegment.start,
        end: outgoingSegment.end,
        position: live.position(),
        rate: liveSnapshot.rate,
      },
      incoming: {
        beats: Array.from(idle.beats?.beats ?? []),
        tempo: idleSnapshot.baseBpm,
        start: incomingSegment.start,
        end: incomingSegment.end,
      },
      config: this.config,
      urgency,
    });

    let method = plan.method;
    let reason = plan.reason;

    if (plan.method === "tempo-ramp" && plan.ramp) {
      // A bend is preparation, not the transition. Once the decks match, the
      // blend is planned afresh and becomes an ordinary beat-aligned one (X13c).
      live.rampToMultiplier(plan.ramp.targetRate, plan.ramp.startAt, plan.ramp.duration);
      this.ramping = true;
      this.rampEndsAt = plan.ramp.startAt + plan.ramp.duration;
      this.history = [
        { title: liveSnapshot.track?.title ?? "", method: "tempo-ramp", reason: plan.reason },
        ...this.history,
      ].slice(0, 8);
      this.emit();
      return;
    }

    if (plan.method === "loop-roll" && plan.roll) {
      for (const slice of plan.roll.slices) {
        live.scheduleSlice(slice.at, slice.offset, slice.duration);
      }
      // Silence the continuous source as the roll takes over, so the roll is
      // heard alone rather than over the track continuing underneath.
      const rollStart = plan.roll.slices[0]?.at ?? plan.startAt;
      live.stopAt(rollStart);
    }

    if (plan.method === "beat-aligned") {
      const setting = idle.setPlaybackMultiplier(plan.incomingRate);
      if (!setting.exact) {
        // The deck could not reach the matched rate. Playing at a nearly-right
        // tempo is worse than not matching at all - it drifts audibly and looks
        // deliberate. Fall back and say so.
        idle.setPlaybackMultiplier(1);
        method = "crossfade";
        reason =
          `deck could not reach ${(plan.incomingRate * 100).toFixed(1)}% ` +
          `(max ${(MAX_TEMPO_RANGE * 100).toFixed(0)}%)`;
      }
    } else {
      idle.setPlaybackMultiplier(1);
    }

    // Everything below is committed to the audio clock, so the transition
    // completes correctly even if this tab stops running JS entirely (X14).
    void idle.playAt(plan.startAt, plan.incomingOffset);
    // Always fade from fully the outgoing deck to fully the incoming one. The
    // fader's resting position is irrelevant - starting from it left the
    // incoming channel already open before the track began.
    const liveSide = live.id === this.firstChannelId() ? -1 : 1;
    this.mixer.scheduleCrossfade(liveSide, -liveSide, plan.startAt, plan.duration);

    this.plan = plan;
    this.planEndsAt = plan.startAt + plan.duration;
    this.history = [
      { title: this.nextTrack.title ?? this.nextTrack.path, method, reason },
      ...this.history,
    ].slice(0, 8);
    this.emit();
  }

  /**
   * Transition to the next track now, rather than at the end of the segment.
   *
   * "smooth" takes a normal blend from the next bar line; "aggressive" rolls
   * out and cuts within a couple of bars.
   */
  async blendNow(urgency: "smooth" | "aggressive"): Promise<void> {
    if (!this.armed) {
      this.message = "Auto-mix is not running.";
      this.emit();
      return;
    }
    // Latched rather than executed here. Pressing the button used to run the
    // whole transition inline, ignoring the loop's busy flag - so a press
    // landing while the loop was already loading a track started a second
    // load on the same deck, each aborting the other, and the press appeared
    // to do nothing at all.
    this.pendingSkip = urgency;
    this.message = urgency === "aggressive" ? "Cutting out…" : "Blending to the next track…";
    this.emit();
    await this.tick();
  }

  private firstChannelId(): string {
    return [...this.mixer.channels.keys()][0] as string;
  }

  private completeTransition(): void {
    const finished = this.liveDeck();
    this.segments.delete(finished.id);
    this.liveId = this.idleDeck().id;
    // Release the finished track's buffer straight away (X15, X20) - two long
    // decoded tracks is over a gigabyte.
    finished.unload();
    this.plan = null;
    this.nextTrack = null;
    this.nextState = "none";
    this.manualHold = false;
  }

  // -- reporting --------------------------------------------------------

  snapshot(): AutoMixSnapshot {
    const live = this.liveDeck().snapshot();
    const countdown = this.plan
      ? Math.max(0, this.plan.startAt - audioContext().currentTime)
      : null;

    return {
      armed: this.armed,
      live: this.liveId,
      nowPlaying: live.track,
      next: this.nextTrack,
      nextState: this.nextState,
      countdown,
      plannedMethod: this.plan?.method ?? null,
      plannedReason: this.plan?.reason ?? null,
      queued: this.selector.state().queued,
      history: this.history,
      usingWorker: this.ticker.usingWorker,
      message: this.message,
      poolSize: this.poolSize,
      poolTag: this.tag,
    };
  }

  private emit(): void {
    this.onChange(this.snapshot());
  }
}
